export const PAGE_IDS: ReadonlyArray<{path: string; pageId: string}> = [
    {
        path  : "/provider/services/account-governance/cloud-accounts",
        pageId: "account-governance-cloud-accounts"
    }
];

export const TRACED_RUNTIME_PATHS: ReadonlyArray<string> = [
    "/provider/services/executivedashboard/finops"
];

export interface TraceIds
{
    pageId: string;
    correlationId: string;
}

const g_componentIds = new WeakMap<object, string>();

let g_currentComponentId: string = null;
let g_activePageId: string     = null;
let g_tracedPath: string       = null;
let g_pageId: string           = null;
let g_correlationId: string    = null;
let g_rotationPending: boolean = false;

export function setActivePageId(pageId: string): void
{
    g_activePageId = pageId || null;
    if (!g_activePageId) g_pageId = null;
}

export function activePageId(): string
{
    return g_activePageId;
}

export function resolveTrace(url: string): TraceIds
{
    const path       = (url || "").split(/[?;]/)[0].replace(/\/+$/, "");
    const staticPage = PAGE_IDS.find(entry => path.endsWith(entry.path));
    const tracedPath = staticPage?.path ?? TRACED_RUNTIME_PATHS.find(traced => path.endsWith(traced)) ?? null;

    if (!tracedPath)
    {
        g_tracedPath = null;
        g_pageId     = null;
        return null;
    }

    const pageId = staticPage?.pageId ?? g_activePageId;

    if (tracedPath !== g_tracedPath || (pageId && g_pageId && pageId !== g_pageId))
    {
        rotateCorrelationId();
    }

    g_tracedPath = tracedPath;
    if (pageId) g_pageId = pageId;

    return {
        pageId,
        correlationId: currentCorrelationId()
    };
}

export const CORRELATION_HEADER_NAME = "X-Correlation-ID";
export const PAGE_HEADER_NAME        = "X-Page-ID";

/**
 * The header pair for a request issued while `routerUrl` is the active route, or null when that route
 * is not traced. Shared with the raw-fetch transports, which never reach the HttpInterceptor chain.
 */
export function traceHeadersFor(routerUrl: string): Record<string, string>
{
    const trace = resolveTrace(routerUrl);
    if (!trace) return null;

    const headers: Record<string, string> = {[CORRELATION_HEADER_NAME]: trace.correlationId};
    if (trace.pageId) headers[PAGE_HEADER_NAME] = trace.pageId;

    return headers;
}

export function currentCorrelationId(): string
{
    if (!g_correlationId) rotateCorrelationId();
    return g_correlationId;
}

export function registerComponentId(owner: object,
                                    componentId: string): void
{
    if (owner && componentId) g_componentIds.set(owner, componentId);
}

export function inheritComponentId(source: object,
                                   target: object): void
{
    const componentId = componentIdOf(source);
    if (componentId) registerComponentId(target, componentId);
}

export function componentIdOf(owner: object): string
{
    return owner ? g_componentIds.get(owner) ?? null : null;
}

export function withComponentId<T>(componentId: string,
                                   call: () => T): T
{
    if (!componentId) return call();

    const previous       = g_currentComponentId;
    g_currentComponentId = componentId;
    try
    {
        return call();
    }
    finally
    {
        g_currentComponentId = previous;
    }
}

export function currentComponentId(): string
{
    return g_currentComponentId;
}

export function rotateCorrelationId(): void
{
    g_correlationId = crypto.randomUUID();
}

export function rotateCorrelationIdForUserAction(): void
{
    if (g_rotationPending) return;

    g_rotationPending = true;
    rotateCorrelationId();

    window.setTimeout(() => g_rotationPending = false);
}
